import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DisclosureToggle, NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { StatusPill } from '@/components/Status';
import { statusPillClass } from '@/lib/status';
import { cn } from '@/lib/utils';
import type { OrderDetail } from '@/api/orders';
import { ACTION_KIND_META } from '../actionKinds';
import { ROUTINE_EVENT_TYPES, eventDetail, eventLabel } from '../eventTemplates';
import { fmtDate } from '../lib';
import { SourceEmailLink } from '../SourceEmailLink';
import { askHref } from '../askLinks';

/**
 * What happened, as a table of facts: each milestone rendered from its typed
 * data, its prose one toggle away. The run's journal (notes, corrections) is
 * most of the log and stays hidden until asked for; once asked for it reads
 * in full, since asking is the request.
 */
export function ActivityTab({ detail }: { detail: OrderDetail }) {
  const location = useLocation();
  const [showRoutine, setShowRoutine] = useState(false);
  const sorted = useMemo(
    () => [...detail.events].sort((a, b) => (b.eventDate || b.createdAt || '').localeCompare(a.eventDate || a.createdAt || '')),
    [detail.events]
  );
  const routineCount = sorted.filter((e) => ROUTINE_EVENT_TYPES.has(e.eventType)).length;
  const rows = showRoutine ? sorted : sorted.filter((e) => !ROUTINE_EVENT_TYPES.has(e.eventType));
  const returnTo = { returnTo: location.pathname + location.search };

  return (
    <div className="space-y-4">
      {detail.actions.length > 0 && (
        <section>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Open decisions</div>
          <div className="divide-y divide-border/40 rounded-md border border-border/60">
            {detail.actions.map((a) => {
              const meta = ACTION_KIND_META[a.kind] ?? ACTION_KIND_META.other;
              return (
                <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                  <Hint content={meta.hint}>
                    <span className={statusPillClass('neutral')}>{meta.label}</span>
                  </Hint>
                  <span className="min-w-0 flex-1">{a.nextStep ?? a.summary}</span>
                  {a.status === 'answered' && <StatusPill tone="info">answered</StatusPill>}
                  <span className="text-xs tabular-nums text-muted-foreground">{fmtDate(a.createdAt)}</span>
                  <Link to={askHref(a.id)} state={returnTo} className="text-xs text-primary hover:underline">Open</Link>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {sorted.length === 0 ? (
        <p className="flex flex-col items-center gap-3 px-6 py-12 text-center text-[14px] text-muted-foreground [&>svg]:size-8">No activity recorded yet.</p>
      ) : (
        <section>
          {routineCount > 0 && (
            <div className="mb-2">
              <DisclosureToggle open={showRoutine} onToggle={() => setShowRoutine((v) => !v)}>
                {routineCount} routine entr{routineCount === 1 ? 'y' : 'ies'}
              </DisclosureToggle>
            </div>
          )}
          {rows.length === 0 ? (
            <p className="text-xs text-muted-foreground">No milestones yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border/60">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="w-20 px-2 py-1.5 text-left font-normal">Date</th>
                    <th className="w-36 px-2 py-1.5 text-left font-normal">Event</th>
                    <th className="px-2 py-1.5 text-left font-normal">Detail</th>
                    <th className="w-40 px-2 py-1.5 text-left font-normal">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => {
                    const routine = ROUTINE_EVENT_TYPES.has(e.eventType);
                    const detailLine = routine ? null : eventDetail(e, detail.currency);
                    const refIds = Array.from(new Set(e.messageRefs.map((r) => r.message_id).filter(Boolean)));
                    return (
                      <tr key={e.id} className={cn('border-t border-border/40 align-top', routine && 'text-muted-foreground')}>
                        <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-muted-foreground">{fmtDate(e.eventDate || e.createdAt)}</td>
                        <td className="px-2 py-1.5 font-medium">{eventLabel(e.eventType)}</td>
                        <td className="px-2 py-1.5">
                          {routine ? (
                            <span className="whitespace-pre-wrap">{e.summary}</span>
                          ) : (
                            <>
                              {detailLine && <div className="tabular-nums">{detailLine}</div>}
                              <NoteDisclosure text={e.summary} className={detailLine ? 'mt-0.5' : undefined} />
                            </>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex flex-wrap gap-1">
                            {refIds.map((id, i) => (
                              <SourceEmailLink key={`${e.id}-${id}`} messageId={id} journeyId={detail.id} label={refIds.length === 1 ? 'email' : `email ${i + 1}`} />
                            ))}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
